import { useState, useCallback } from 'react';
import {
  TeacherLoginResponse,
  TeacherLoginDetailResponse,
  TeacherLoginQueryParams,
  ESnackbarStyle,
} from 'shared/types';
import { openNotification } from 'utils/notification-utils';
import { getTeacherLogins, getTeacherLoginDetail } from 'services/api/teacherLoginService';
import { useDebounce } from 'shared/hooks';

interface TeacherLoginsDataResult {
  data: TeacherLoginResponse | null;
  detailData: TeacherLoginDetailResponse | null;
  isLoading: boolean;
  isDetailLoading: boolean;
  filterParams: TeacherLoginQueryParams;
  setFilterParams: (params: TeacherLoginQueryParams) => void;
  fetchData: () => Promise<void>;
  fetchDetail: (userId: string) => Promise<void>;
}

const useTeacherLoginsData = (): TeacherLoginsDataResult => {
  const [data, setData] = useState<TeacherLoginResponse | null>(null);
  const [detailData, setDetailData] = useState<TeacherLoginDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDetailLoading, setIsDetailLoading] = useState<boolean>(false);
  const [filterParams, setFilterParams] = useState<TeacherLoginQueryParams>({});

  const debouncedParams = useDebounce(filterParams, 500);

  const fetchData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await getTeacherLogins(debouncedParams);
      setData(response.data);
    } catch (e: any) {
      openNotification(ESnackbarStyle.ERROR, e.message || 'Failed to fetch teacher logins');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedParams]);

  const fetchDetail = useCallback(
    async (userId: string): Promise<void> => {
      setIsDetailLoading(true);
      try {
        const response = await getTeacherLoginDetail(userId, {
          dateFrom: debouncedParams.dateFrom,
          dateTo: debouncedParams.dateTo,
        });
        setDetailData(response.data);
      } catch (e: any) {
        openNotification(ESnackbarStyle.ERROR, e.message || 'Failed to fetch login detail');
      } finally {
        setIsDetailLoading(false);
      }
    },
    [debouncedParams.dateFrom, debouncedParams.dateTo]
  );

  return {
    data,
    detailData,
    isLoading,
    isDetailLoading,
    filterParams,
    setFilterParams,
    fetchData,
    fetchDetail,
  };
};

export default useTeacherLoginsData;
